import React, { useState } from 'react';
import { useAlert } from '../context/AlertContext';
import { createRSVP } from '../services/rsvpService';
import api from '../services/api';

const RSVPForm = ({ event, onClose, onSuccess }) => {
  const { showAlert } = useAlert();
  const [isProcessing, setIsProcessing] = useState(false);
  const [waitlistRsvp, setWaitlistRsvp] = useState(null); // {id, eventId, email}
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    specialty: '',
    guests: 1,
  });

  const EMPLOYEE_EMAIL_DOMAINS = ['@laytonconstruction.com', '@q.com', '@qtsdatacenters.com'];
  const emailLower = formData.email.toLowerCase();
  const isLaytonEmail = EMPLOYEE_EMAIL_DOMAINS.some((d) => emailLower.endsWith(d));
  const registrationType = isLaytonEmail ? 'employee' : 'contractor';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      const rsvpData = {
        eventId: event.id,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        specialty: formData.specialty || null,
        registrationType,
        guests: parseInt(formData.guests),
      };

      const response = await createRSVP(rsvpData);

      if (registrationType === 'contractor' && response.checkoutUrl) {
        window.location.href = response.checkoutUrl;
      } else if (registrationType === 'contractor') {
        showAlert('Your registration is received! Our team will contact you with payment instructions shortly.', 'success');
        onSuccess();
        onClose();
      } else if (response.waitlisted) {
        setWaitlistRsvp({ id: response.id, eventId: event.id, email: formData.email });
        setIsProcessing(false);
      } else {
        showAlert('RSVP confirmed! See you at the tournament!', 'success');
        onSuccess();
        onClose();
      }
    } catch (error) {
      showAlert(error.message || 'Error processing RSVP. Please try again.', 'error');
      setIsProcessing(false);
    }
  };

  const handleCancelWaitlist = async () => {
    setIsProcessing(true);
    try {
      await api.delete(`/rsvps/${waitlistRsvp.eventId}/${waitlistRsvp.id}?email=${encodeURIComponent(waitlistRsvp.email)}`);
      showAlert('Your registration has been cancelled.', 'success');
      onClose();
    } catch {
      showAlert('Could not cancel. Please contact us directly.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const totalAmount = event.fee * formData.guests;

  if (waitlistRsvp) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>📋</div>
        <h3 style={{ color: '#92400e', marginBottom: '12px' }}>You're on the Waitlist</h3>
        <p style={{ color: '#666', marginBottom: '8px', lineHeight: '1.6' }}>
          All spots are currently filled. We've recorded your interest and will notify you by email if a spot opens up.
        </p>
        <p style={{ color: '#666', marginBottom: '28px', fontSize: '0.9rem' }}>
          You'll receive a waitlist confirmation email shortly.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={handleCancelWaitlist}
            disabled={isProcessing}
            style={{ padding: '10px 22px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' }}
          >
            {isProcessing ? 'Cancelling...' : "No thanks, cancel my spot"}
          </button>
          <button
            onClick={onClose}
            style={{ padding: '10px 22px', background: '#1b4d3e', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif' }}
          >
            I'll wait — keep my spot
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="form-label">Email *</label>
        <input
          type="email"
          name="email"
          className="form-input"
          value={formData.email}
          onChange={handleChange}
          placeholder="your@email.com"
          required
        />
        {formData.email && (
          <p style={{ marginTop: '6px', fontSize: '13px', fontWeight: '600', color: isLaytonEmail ? '#2d6b52' : '#888' }}>
            {isLaytonEmail ? '✓ Layton employee — complimentary registration' : 'Registration fee: $300'}
          </p>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">Full Name *</label>
        <input
          type="text"
          name="name"
          className="form-input"
          value={formData.name}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label className="form-label">Phone *</label>
        <input
          type="tel"
          name="phone"
          className="form-input"
          value={formData.phone}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label className="form-label">Company</label>
        <input
          type="text"
          name="company"
          className="form-input"
          value={formData.company}
          onChange={handleChange}
        />
      </div>

      {!isLaytonEmail && (
        <div className="form-group">
          <label className="form-label">What's your specialty? (Optional)</label>
          <textarea
            name="specialty"
            className="form-textarea"
            value={formData.specialty}
            onChange={handleChange}
            placeholder="Tell us about your construction specialty, what you do, or what you're interested in..." 
            rows="3"
            style={{ resize: 'vertical' }}
          />
          <p style={{ marginTop: '6px', fontSize: '12px', color: '#888', fontStyle: 'italic' }}>
            Help us connect you with the right people! Share your trade, expertise, or interests.
          </p>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Number of Guests</label>
        <input
          type="number"
          name="guests"
          className="form-input"
          min="1"
          max="4"
          value={formData.guests}
          onChange={handleChange}
        />
        {registrationType === 'contractor' && formData.guests > 1 && (
          <p style={{ marginTop: '8px', color: '#666' }}>
            Total: ${totalAmount.toFixed(2)}
          </p>
        )}
      </div>

      {registrationType === 'contractor' && (
        <div className="payment-info">
          <strong>Payment Required</strong>
          <div className="payment-amount">${totalAmount.toFixed(2)}</div>
          <p>You will be redirected to secure payment after form submission</p>
        </div>
      )}

      <button type="submit" className="submit-button" disabled={isProcessing}>
        {isProcessing
          ? 'Processing...'
          : registrationType === 'contractor'
          ? 'Proceed to Payment'
          : 'Confirm RSVP'}
      </button>
    </form>
  );
};

export default RSVPForm;
